import React, { createContext, useContext, useState, useCallback } from 'react';
import { useAuth } from './authContext';

const BusinessDeletionContext = createContext();

export const useBusinessDeletion = () => {
  const context = useContext(BusinessDeletionContext);
  if (!context) {
    throw new Error('useBusinessDeletion must be used within a BusinessDeletionProvider');
  }
  return context;
};

export const BusinessDeletionProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [subscriptionWarning, setSubscriptionWarning] = useState(null);
  const { makeAuthenticatedRequest, loginType } = useAuth();

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:9090';

  // =================================================================
  // CORRECTED: Use business-data service, not business-deletion
  // =================================================================
  const getDeletionBaseUrl = useCallback(() => {
    if (loginType !== 'master') {
      throw new Error('Access denied: Business deletion is a master-level-only function.');
    }
    
    // CORRECTED: The deletion endpoint is part of business-data service
    const servicePath = 'business-data/api/v1/business-data/registry';
    
    return `${API_BASE_URL}/master/${servicePath}`;
  }, [API_BASE_URL, loginType]);

  // NEW: Get the cascade delete base URL (separate endpoint)
  const getCascadeDeletionBaseUrl = useCallback(() => {
    if (loginType !== 'master') {
      throw new Error('Access denied: Business deletion is a master-level-only function.');
    }
    
    const servicePath = 'business-data/api/v1/business-data/cascade-delete/registry';
    
    return `${API_BASE_URL}/master/${servicePath}`;
  }, [API_BASE_URL, loginType]);

  // Improved error handling for different response types
  const handleApiResponse = async (response) => {
    // Handle 409 Conflict (active subscription without force)
    if (response.status === 409) {
      const data = await response.json();
      setSubscriptionWarning(data);
      throw new Error('SUBSCRIPTION_CONFLICT');
    }

    // Handle other non-ok statuses
    if (!response.ok) {
      let errorMessage;
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorData.message || `Request failed with status ${response.status}`;
      } catch {
        errorMessage = await response.text() || `Request failed with status ${response.status}`;
      }
      throw new Error(errorMessage);
    }

    // Handle 204 No Content success case
    if (response.status === 204) {
      return { 
        success: true,
        status: 'deleted', 
        message: 'Business deleted successfully.' 
      };
    }

    // Parse JSON response for 200 OK
    return await response.json();
  };

  const validateAccess = useCallback(() => {
    if (loginType !== 'master') {
      throw new Error('Access denied: Only master users can delete businesses.');
    }
  }, [loginType]);

  /**
   * Delete a business by registry number
   * @param {string} registryNumber - The business registry number
   * @param {boolean} force - If true, will cancel active subscriptions and delete
   * @returns {Promise<object>} Deletion result
   */
  const deleteBusiness = useCallback(async (registryNumber, force = false) => {
    validateAccess();
    
    if (!registryNumber) {
      throw new Error('Business registry number is required.');
    }

    setLoading(true);
    setError(null);
    setSubscriptionWarning(null);

    try {
      const baseUrl = getDeletionBaseUrl();
      // Add force parameter if needed
      const url = force 
        ? `${baseUrl}/${encodeURIComponent(registryNumber)}?force=true`
        : `${baseUrl}/${encodeURIComponent(registryNumber)}`;

      console.log(`Deleting business: ${registryNumber}, force: ${force}`);

      const response = await makeAuthenticatedRequest(url, {
        method: 'DELETE',
      });

      const result = await handleApiResponse(response);
      
      console.log('Deletion successful:', result);
      return result;

    } catch (err) {
      // Special handling for subscription conflict
      if (err.message === 'SUBSCRIPTION_CONFLICT') {
        console.log('Subscription conflict detected:', subscriptionWarning);
        throw err; // Re-throw to be handled by calling component
      }

      const errorMessage = err.message || 'Failed to delete the business.';
      setError(errorMessage);
      console.error('Deletion error:', errorMessage);
      throw new Error(errorMessage);

    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, getDeletionBaseUrl, validateAccess, subscriptionWarning]);

  /**
   * Delete business with force (cancels active subscription)
   * Convenience method that calls deleteBusiness with force=true
   */
  const forceDeleteBusiness = useCallback(async (registryNumber) => {
    return await deleteBusiness(registryNumber, true);
  }, [deleteBusiness]);

  /**
   * NEW: Force cascade delete - Deletes ALL business data including subscription tables
   * This is the nuclear option - irreversible and comprehensive
   * @param {string} registryNumber - The business registry number
   * @param {boolean} confirmed - Must be true to proceed (safety check)
   * @returns {Promise<object>} Deletion result with comprehensive details
   */
  const forceCascadeDeleteBusiness = useCallback(async (registryNumber, confirmed = false) => {
    validateAccess();
    
    if (!registryNumber) {
      throw new Error('Business registry number is required.');
    }

    if (!confirmed) {
      throw new Error('Force cascade delete requires explicit confirmation. This action cannot be undone.');
    }

    setLoading(true);
    setError(null);
    setSubscriptionWarning(null);

    try {
      const baseUrl = getCascadeDeletionBaseUrl();
      // IMPORTANT: Must include the confirmation parameter
      const url = `${baseUrl}/${encodeURIComponent(registryNumber)}?confirm=DELETE_ALL_DATA`;

      console.warn(`⚠️ FORCE CASCADE DELETE initiated for business: ${registryNumber}`);
      console.warn('⚠️ This will delete ALL data including subscription tables - IRREVERSIBLE');

      const response = await makeAuthenticatedRequest(url, {
        method: 'DELETE',
      });

      const result = await handleApiResponse(response);
      
      console.log('✅ Force cascade deletion successful:', result);
      console.log('📊 Deleted tables:', result.deleted_tables);
      
      return {
        ...result,
        deletionType: 'FORCE_CASCADE',
        warning: 'All data has been permanently deleted and cannot be recovered'
      };

    } catch (err) {
      const errorMessage = err.message || 'Failed to cascade delete the business.';
      setError(errorMessage);
      console.error('❌ Force cascade deletion error:', errorMessage);
      throw new Error(errorMessage);

    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, getCascadeDeletionBaseUrl, validateAccess]);

  /**
   * Check if a business can be deleted (checks for active subscriptions)
   * This is a pre-check before showing delete confirmation
   */
  const checkDeletionEligibility = useCallback(async (registryNumber) => {
    validateAccess();

    if (!registryNumber) {
      throw new Error('Business registry number is required.');
    }

    try {
      // First try to delete without force to check for subscriptions
      const baseUrl = getDeletionBaseUrl();
      const url = `${baseUrl}/${encodeURIComponent(registryNumber)}`;

      const response = await makeAuthenticatedRequest(url, {
        method: 'DELETE',
      });

      if (response.status === 409) {
        const data = await response.json();
        return {
          canDelete: false,
          hasActiveSubscription: true,
          subscriptionInfo: data.details,
          message: data.error
        };
      }

      // If we get here, the business can be deleted
      return {
        canDelete: true,
        hasActiveSubscription: false,
        subscriptionInfo: null,
        message: 'Business can be deleted'
      };

    } catch (err) {
      console.error('Error checking deletion eligibility:', err);
      return {
        canDelete: false,
        hasActiveSubscription: false,
        subscriptionInfo: null,
        message: err.message || 'Unable to check deletion eligibility'
      };
    }
  }, [makeAuthenticatedRequest, getDeletionBaseUrl, validateAccess]);

  const clearError = useCallback(() => {
    setError(null);
    setSubscriptionWarning(null);
  }, []);

  const value = {
    // State
    loading,
    error,
    subscriptionWarning,
    isMasterUser: loginType === 'master',
    
    // Methods
    deleteBusiness,
    forceDeleteBusiness,
    forceCascadeDeleteBusiness, // NEW: Nuclear option for complete data deletion
    checkDeletionEligibility,
    clearError,
  };

  return (
    <BusinessDeletionContext.Provider value={value}>
      {children}
    </BusinessDeletionContext.Provider>
  );
};